/**
 * Home utility-rate estimation (informational).
 *
 * The owner's electricity price never bills anyone — home charging is
 * free. These helpers turn the owner-entered rate structure (flat /
 * time-of-use / tiered) into the "what does this charging cost me at
 * home" estimates shown on the My Home report and the session receipt.
 * All client-side, mirroring the server's display-only stance: the
 * structure lives on the home tenant and is never read by billing.
 *
 * Pure module on purpose (no imports): api.ts imports these types for
 * the /me/home payload, so an import back into api.ts would cycle.
 */

export interface HomeTouWindow {
  /** 0-23, inclusive start of the window. */
  start_hour: number;
  /** 1-24, exclusive end (24 = midnight). An end at or before the
   *  start wraps midnight: 19→7 covers [19,24) ∪ [0,7). */
  end_hour: number;
  rate_cents_per_kwh: number;
  /** Days this window applies to (0=Sun..6=Sat, the guest-window
   *  convention). Absent/empty = every day. Matching is by CALENDAR
   *  day of each hour: a Sat/Sun window covering 19→7 applies Sat
   *  19:00-24:00 and Sat 00:00-07:00 (not Monday's small hours). */
  days_of_week?: number[] | null;
}

export interface HomeRateTier {
  /** Monthly kWh boundary this bracket runs up to; null = the final
   *  open-ended bracket. */
  up_to_kwh: number | null;
  rate_cents_per_kwh: number;
}

export type HomeRateType = 'flat' | 'time_of_use' | 'tiered';

export interface HomeUtilityRate {
  rate_type: HomeRateType;
  /** Flat price, and the base/off-peak fallback under time_of_use. */
  rate_cents_per_kwh: number | null;
  tou_windows?: HomeTouWindow[] | null;
  tiers?: HomeRateTier[] | null;
}

/** Build the estimation input from the /me/home tenant payload shape. */
export function homeRateFromTenant(tenant: {
  owner_utility_rate_cents_per_kwh: number | null;
  owner_utility_rate_type?: HomeRateType;
  owner_utility_tou_windows?: HomeTouWindow[] | null;
  owner_utility_tiers?: HomeRateTier[] | null;
} | null | undefined): HomeUtilityRate | null {
  if (!tenant) return null;
  return {
    rate_type: tenant.owner_utility_rate_type ?? 'flat',
    rate_cents_per_kwh: tenant.owner_utility_rate_cents_per_kwh,
    tou_windows: tenant.owner_utility_tou_windows ?? null,
    tiers: tenant.owner_utility_tiers ?? null,
  };
}

/** True when the structure has enough numbers to estimate with. */
export function hasUsableHomeRate(rate: HomeUtilityRate | null | undefined): rate is HomeUtilityRate {
  if (!rate) return false;
  if (rate.rate_type === 'tiered') return (rate.tiers?.length ?? 0) > 0;
  return rate.rate_cents_per_kwh != null;
}

/** Rate in effect at a (calendar day, hour): the covering TOU window,
 *  else base. A window whose end is at or before its start crosses
 *  midnight and covers [start,24) ∪ [0,end) — Ontario's 7 PM–7 AM
 *  off-peak. Without the wrap branch such a window would never match
 *  any hour and every overnight kWh would silently price at base.
 *  days_of_week absent/empty = every day; matching is by the calendar
 *  day the hour falls on. */
function touRateAt(rate: HomeUtilityRate, day: number, hour: number): number {
  for (const w of rate.tou_windows ?? []) {
    const days = w.days_of_week ?? [];
    if (days.length > 0 && !days.includes(day)) continue;
    const inWindow = w.end_hour > w.start_hour
      ? hour >= w.start_hour && hour < w.end_hour
      : hour >= w.start_hour || hour < w.end_hour;
    if (inWindow) return w.rate_cents_per_kwh;
  }
  return rate.rate_cents_per_kwh ?? 0;
}

export interface EstimableSession {
  started_at: string | Date;
  duration_minutes?: number | null;
  energy_kwh?: number | null;
}

/**
 * Estimated utility cost of one session, in cents (fractional; round at
 * the display edge). Null when the rate isn't usable.
 *   flat         kWh × rate.
 *   time_of_use  energy spread uniformly over the session's wall-clock
 *                span; each hour slice priced by its window (base
 *                outside every window). Zero/unknown duration prices
 *                everything at the start hour.
 *   tiered       session kWh walked through the monthly brackets
 *                starting at `monthKwhBefore` (home-charging kWh
 *                already used this calendar month; 0 when unknown).
 */
export function estimateSessionUtilityCents(
  rate: HomeUtilityRate | null | undefined,
  session: EstimableSession,
  monthKwhBefore = 0,
): number | null {
  if (!hasUsableHomeRate(rate)) return null;
  const kwh = session.energy_kwh ?? 0;
  if (kwh <= 0) return 0;

  if (rate.rate_type === 'time_of_use') {
    const start = new Date(session.started_at).getTime();
    const durMs = Math.max(0, (session.duration_minutes ?? 0) * 60_000);
    if (!Number.isFinite(start) || durMs === 0) {
      const d = Number.isFinite(start) ? new Date(start) : null;
      return kwh * touRateAt(rate, d?.getDay() ?? 0, d?.getHours() ?? 0);
    }
    // Uniform energy over time: walk the hour boundaries the session
    // crosses and price each slice by its (day, hour) rate — the day
    // matters for weekend/weekday windows and flips at midnight.
    let cents = 0;
    let cursor = start;
    const end = start + durMs;
    while (cursor < end) {
      const d = new Date(cursor);
      const nextTop = new Date(cursor);
      nextTop.setMinutes(60, 0, 0); // top of the next hour
      const sliceEnd = Math.min(nextTop.getTime(), end);
      cents += kwh * ((sliceEnd - cursor) / durMs) * touRateAt(rate, d.getDay(), d.getHours());
      cursor = sliceEnd;
    }
    return cents;
  }

  if (rate.rate_type === 'tiered') {
    const tiers = rate.tiers ?? [];
    let remaining = kwh;
    let used = Math.max(0, monthKwhBefore);
    let cents = 0;
    for (const t of tiers) {
      if (remaining <= 0) break;
      const room = t.up_to_kwh == null ? Infinity : Math.max(0, t.up_to_kwh - used);
      const take = Math.min(remaining, room);
      if (take > 0) {
        cents += take * t.rate_cents_per_kwh;
        remaining -= take;
        used += take;
      }
    }
    if (remaining > 0 && tiers.length > 0) {
      // Defensive: the server enforces an open-ended last bracket, but a
      // malformed list prices the overflow at the last bracket's rate.
      cents += remaining * tiers[tiers.length - 1]!.rate_cents_per_kwh;
    }
    return cents;
  }

  return kwh * (rate.rate_cents_per_kwh ?? 0);
}

/**
 * Per-session estimates for a list of home sessions, keyed by _id.
 * Tiered brackets accumulate per calendar month in chronological order,
 * so pass EVERY known home session (not just the displayed period) for
 * honest month-to-date allocation, then read only the ids you display.
 */
export function estimateSessionsUtilityCents(
  rate: HomeUtilityRate | null | undefined,
  sessions: Array<EstimableSession & { _id: string }>,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!hasUsableHomeRate(rate)) return out;
  const chrono = [...sessions].sort(
    (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
  );
  let monthKey = '';
  let monthKwh = 0;
  for (const s of chrono) {
    const d = new Date(s.started_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== monthKey) {
      monthKey = key;
      monthKwh = 0;
    }
    out.set(s._id, estimateSessionUtilityCents(rate, s, monthKwh) ?? 0);
    monthKwh += s.energy_kwh ?? 0;
  }
  return out;
}

/**
 * Short human phrase for "at <this rate>" copy: "$0.15/kWh" for flat,
 * "your time-of-use rates" / "your tiered rates" for the structures.
 * Null when the rate isn't usable.
 *
 * `formatCents` prints the flat rate in the home's currency (item 3:
 * pass money.ts formatMoney with the home's currency and the app
 * language, so a Canadian home reads "CA$0.15/kWh"). Absent: "$0.15",
 * the USD form. Passed in, so this module stays import-free.
 */
export function describeHomeRate(
  rate: HomeUtilityRate | null | undefined,
  formatCents: (cents: number) => string = (cents) => `$${(cents / 100).toFixed(2)}`,
): string | null {
  if (!hasUsableHomeRate(rate)) return null;
  if (rate.rate_type === 'time_of_use') return 'your time-of-use rates';
  if (rate.rate_type === 'tiered') return 'your tiered rates';
  return `${formatCents(rate.rate_cents_per_kwh ?? 0)}/kWh`;
}
